import React, {useEffect, useState} from 'react';
import st from './Statuses.module.scss';
import Table from '../../components/Table/Table';
import ModalForm from '../../components/ModalForm/ModalForm';
import {Input, Button, Popconfirm, notification, Select} from "antd";
import api from '../../config/axiosConfig';
import {EditOutlined, DeleteOutlined} from '@ant-design/icons';

const Statuses = () => {
    const [statuses, setStatuses] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusIsUpdated, setStatusIsUpdated] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [isActionBased, setIsActionBased] = useState(false);
    const actionBasedStatuses = statuses.filter(status => status.isActionBased);
    const nonActionBasedStatuses = statuses.filter(status => !status.isActionBased);

    const fetchStatuses = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('statuses', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setStatuses(response.data);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch statuses.',
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddEditStatus = async (values) => {
        setLoading(true);
        try {
            if (currentStatus) {
                // Edit Status
                await api.put(`statuses/${currentStatus.id}`, values);
                notification.success({
                    message: 'Success',
                    description: 'Status updated successfully',
                    placement: 'topRight'
                });
            } else {
                // Add Status
                await api.post('statuses/create', {
                    ...values,
                    isActionBased
                });
                notification.success({
                    message: 'Success',
                    description: 'Status added successfully',
                    placement: 'topRight'
                });
            }
            setIsModalOpen(false);
            setStatusIsUpdated(!statusIsUpdated);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to save status. Please try again.',
                placement: 'topRight'
            });
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (status) => {
        setCurrentStatus(status);
        setEditMode(true);
        setIsModalOpen(true);
    };

    const handleDeleteStatus = async (id) => {
        setLoading(true);
        try {
            await api.delete(`statuses/${id}`);
            notification.success({
                message: 'Success',
                description: 'Status has been successfully deleted.',
                placement: 'topRight',
            });
            setStatusIsUpdated(prev => !prev);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete the status. Please try again.',
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatuses();
    }, [statusIsUpdated]);

    const columns = [
        {
            title: 'სტატუსი',
            dataIndex: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'კომენტარი',
            dataIndex: 'note',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text, record) => (
                <>
                    {/* Edit Button */}
                    <Button type="link" onClick={() => openEditModal(record)}>
                        <EditOutlined/>
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this team?"
                        onConfirm={() => handleDeleteStatus(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger>
                            <DeleteOutlined/>
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];
    const formItems = [
        [{label: 'სტატუსი', name: 'name', rules: [{required: true}], component: <Input/>, span: 24}],
        [{label: 'კომენტარი', name: 'note', component: <Input.TextArea/>, span: 24}]
    ];

    return (
        <div className={st.statusesTableContainer}>
            <div className={st.nonActBased}>
                <div className={st.btnContainer}>
                    <Button type="primary" onClick={() => {
                        setIsModalOpen(true)
                        setEditMode(false);
                        setCurrentStatus(null);
                        setIsActionBased(false);
                    }} style={{marginBottom: 16}}>
                        Add Non Action Based Status
                    </Button>
                </div>
                <ModalForm
                    open={isModalOpen} // Updated to `open`
                    title={editMode ? "Edit Status" : "Add New Status"}
                    onCancel={() => {
                        setIsModalOpen(false)
                        setIsActionBased(false)
                    }}
                    onSubmit={handleAddEditStatus}
                    confirmLoading={loading}
                    formItems={formItems}
                    initialValues={currentStatus}
                    rowIsUpdated={statusIsUpdated}
                />
                <Table columns={columns} url={null} rowIsUpdated={statusIsUpdated} dataSource={nonActionBasedStatuses}/>
            </div>
            <div className={st.actBased}>
                <div className={st.btnContainer}>
                    <Button type="primary" onClick={() => {
                        setIsModalOpen(true)
                        setEditMode(false);
                        setCurrentStatus(null);
                        setIsActionBased(true);
                    }} style={{marginBottom: 16}}>
                        Add Action Based Status
                    </Button>
                </div>
                <Table columns={columns} url={null} rowIsUpdated={statusIsUpdated} dataSource={actionBasedStatuses}/>
            </div>
        </div>
    )
}

export default Statuses;